<!--
Auto-generated by: https://github.com/threlte/threlte/tree/main/packages/gltf
Command: npx @threlte/gltf@2.0.3 T3_separatedBody.glb -u -s -t -P
-->
<script module lang="ts">
  import { Group, Material, Mesh, MeshStandardMaterial } from 'three';
  import { T, type Props } from '@threlte/core';
  import { useGltf, useSuspense } from '@threlte/extras';
  import type { Snippet } from 'svelte';

  const scale = 0.75;

  type GLTFResult = {
    nodes: {
      backWhitePlastic: Mesh;
      blackFrame: Mesh;
      button: Mesh;
      Cameras: Mesh;
      frontCamera: Mesh;
      glass: Mesh;
      logos: Mesh;
      Screen: Mesh;
      sideDetails: Mesh;
      sidePort: Mesh;
      whiteFrame: Mesh;
    };
    materials: {};
  };

  let modelUrl = '';

  const load = () => {
    const suspend = useSuspense();
    return suspend(useGltf<GLTFResult>(modelUrl));
  };

  export const preload = async (url: string) => {
    modelUrl = url;
    try {
      await load();
    } catch (err) {
      return console.error('Failed to load model', err);
    }
  };
</script>

<script lang="ts">
  let {
    fallback,
    error,
    children,
    ref = $bindable(),
    matcaps,
    fallbackMaterial,
    ...props
  }: Props<Group> & {
    ref?: Group;
    matcaps: {
      blackFrame: Material;
      t3Glass: Material;
      whitePlastic: Material;
      t3_screen: Material;
      button: Material;
      squareLogo: Material;
      cameraFront: Material;
      t3_whiteDiffuse: Material;
      t3_cameras: Material;
    };
    fallbackMaterial: Material;
    children?: Snippet<[{ ref: Group | undefined }]>;
    fallback?: Snippet;
    error?: Snippet<[{ error: Error }]>;
  } = $props();
  let materials: any = $state(matcaps);
  $effect(() => {
    materials = matcaps ?? {
      t3Glass: fallbackMaterial,
      blackFrame: fallbackMaterial,
      whitePlastic: fallbackMaterial,
      t3_screen: fallbackMaterial,
      button: fallbackMaterial,
      squareLogo: fallbackMaterial,
      cameraFront: fallbackMaterial,
      t3_whiteDiffuse: fallbackMaterial,
      t3_cameras: fallbackMaterial,
    };
  });
  const gltf = load();
</script>

<T is={new Group()} dispose={false} {...props} bind:ref {scale}>
  {#await gltf}
    {@render fallback?.()}
  {:then gltf}
    <T.Group is={new Group()} rotation={[Math.PI / 2, 0, -Math.PI / 2]} scale={0.1}>
      <T.Mesh geometry={gltf.nodes.backWhitePlastic.geometry} material={materials.whitePlastic} />
      <T.Mesh geometry={gltf.nodes.blackFrame.geometry} material={materials.blackFrame} />
      <T.Mesh geometry={gltf.nodes.button.geometry} material={materials.button} />
      <T.Mesh geometry={gltf.nodes.Cameras.geometry} material={materials.t3_cameras} />
      <T.Mesh geometry={gltf.nodes.frontCamera.geometry} material={materials.cameraFront} />
      <T.Mesh geometry={gltf.nodes.glass.geometry} material={materials.t3Glass} />
      <T.Mesh geometry={gltf.nodes.logos.geometry} material={materials.squareLogo} />
      <T.Mesh geometry={gltf.nodes.Screen.geometry} material={materials.t3_screen} position={[0, 0.0005, 0]} />
      <T.Mesh
        geometry={gltf.nodes.Screen.geometry}
        material={materials.blackFrame}
        position={[0, 0.1, 0]}
        scale={1.1}
      />
      <T.Mesh geometry={gltf.nodes.sideDetails.geometry} material={materials.blackFrame} />
      <T.Mesh geometry={gltf.nodes.sidePort.geometry} material={materials.button} />
      <T.Mesh geometry={gltf.nodes.whiteFrame.geometry} material={materials.t3_whiteDiffuse} />
    </T.Group>
  {:catch err}
    {@render error?.({ error: err })}
  {/await}

  {@render children?.({ ref })}
</T>
